import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { Button, Collapse, message } from "antd";
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  FilePdfOutlined,
  FileTextOutlined,
  MessageOutlined,
  LinkOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  ThunderboltFilled,
  TeamOutlined,
} from "@ant-design/icons";
import "../style/public.scss";
import apiClient from "../actions/api";
import { loadUser } from "../actions/auth";

const services = [
  {
    key: "answers",
    icon: <MessageOutlined />,
    eyebrow: "Application Assistant",
    title: "AI-powered answers",
    description:
      "Get a relevant, polished response to each application question, based on your experience and the role.",
    priceKey: "answer",
    originalPrice: 0.005,
    unit: "per answer",
    features: [
      "Answers tailored to the application question",
      "Uses your resume and profile context",
      "Charged only when an answer is generated",
    ],
  },
  {
    key: "resume",
    icon: <FileTextOutlined />,
    eyebrow: "Tailored Resume",
    title: "A resume for every role",
    description:
      "Create a job-specific resume that highlights the most relevant skills and experience for the position.",
    priceKey: "resume",
    originalPrice: 0.025,
    unit: "per resume",
    features: [
      "Tailored to the job description",
      "Professional DOCX and PDF included",
      "One charge covers both file formats",
    ],
  },
  {
    key: "cover-letter",
    icon: <FilePdfOutlined />,
    eyebrow: "Tailored Cover Letter",
    title: "Make every introduction count",
    description:
      "Generate a focused cover letter that connects your background with the employer's needs.",
    priceKey: "coverLetter",
    originalPrice: 0.01,
    unit: "per cover letter",
    features: [
      "Personalized for the role and company",
      "Ready-to-send PDF included",
      "Charged per generated cover letter",
    ],
  },
  {
    key: "job-url",
    icon: <LinkOutlined />,
    eyebrow: "Job Board",
    title: "Unlock a job application",
    description:
      "Open or copy the original application URL for a job discovered through the Job Board.",
    priceKey: "jobUrl",
    originalPrice: 0.0025,
    unit: "per unique job",
    features: [
      "Use either Apply or Copy URL",
      "One purchase unlocks both actions",
      "Never charged twice for the same job",
    ],
  },
];

const faqItems = [
  {
    key: "1",
    label: "How does pay-as-you-go pricing work?",
    children: (
      <p>
        Standard membership has no subscription. Content generation remains pay
        as you go; the optional Professional team plan renews monthly.
      </p>
    ),
  },
  {
    key: "2",
    label: "What counts as one application answer?",
    children: (
      <p>
        Each response generated by the Application Assistant for one application
        question uses the current per-answer price shown above. Viewing or
        editing an existing answer does not create another charge.
      </p>
    ),
  },
  {
    key: "3",
    label: "Are both resume file formats included?",
    children: (
      <p>
        Yes. A tailored-resume generation includes both DOCX and PDF versions of
        that resume at no additional cost.
      </p>
    ),
  },
  {
    key: "4",
    label: "What format is the cover letter?",
    children: (
      <p>
        Each tailored cover letter is generated as a polished PDF, ready to
        review and use with your application.
      </p>
    ),
  },
];

const Pricing = ({ title }) => {
  document.title = title;
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const authUser = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const [membershipPrice, setMembershipPrice] = useState(10);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [purchasingMembership, setPurchasingMembership] = useState(false);
  const [prices, setPrices] = useState({
    answer: 0.004,
    resume: 0.02,
    coverLetter: 0.005,
    jobUrl: 0.002,
  });

  useEffect(() => {
    apiClient
      .get("/pricing")
      .then(({ data }) => setPrices(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    apiClient
      .get("/membership/plans")
      .then(({ data }) => {
        setMembershipPrice(Number(data.professional || 10));
      })
      .catch(() => {});
    if (isAuthenticated) {
      apiClient
        .get("/membership/status")
        .then(({ data }) => setMembershipStatus(data))
        .catch(() => {});
    }
  }, [isAuthenticated]);

  const purchaseProfessional = async () => {
    setPurchasingMembership(true);
    try {
      const { data } = await apiClient.post("/membership/professional");
      setMembershipStatus((current) => ({
        ...current,
        membership: data.membership,
        active: true,
        group: data.group,
        balance: data.balance,
      }));
      await dispatch(loadUser());
      message.success(
        "Professional membership activated. Your new team is ready.",
      );
    } catch (error) {
      message.error(
        error.response?.data?.errors?.[0]?.msg ||
          "Unable to purchase Professional membership.",
      );
    } finally {
      setPurchasingMembership(false);
    }
  };

  const formatPrice = (value) =>
    `$${Number(value || 0)
      .toFixed(6)
      .replace(/0+$/, "")
      .replace(/\.$/, ".00")}`;
  const exampleTotal = useMemo(
    () => prices.answer * 5 + prices.resume + prices.coverLetter,
    [prices],
  );

  return (
    <div className="public-page pricing-page">
      <section
        className="membership-section"
        aria-labelledby="membership-title"
      >
        <div className="membership-heading">
          <span className="pricing-card-eyebrow">Membership</span>
          <h1 id="membership-title">Choose how you work</h1>
          <p>
            Start free with personal tools, then upgrade when you are ready to
            lead a team.
          </p>
        </div>
        <div className="membership-grid">
          <article className="membership-card">
            <span className="membership-plan-label">Standard</span>
            <div className="membership-price">
              <strong>$0</strong>
              <span>forever</span>
            </div>
            <p>Everything you need to manage your own job search.</p>
            <ul>
              {[
                "Resume and cover-letter generation",
                "AI application answers",
                "Job Board access",
                "Personal interviews and calendar",
                "Personal statistics",
              ].map((feature) => (
                <li key={feature}>
                  <CheckCircleFilled /> {feature}
                </li>
              ))}
            </ul>
            <div className="membership-current-label">
              {!membershipStatus?.active
                ? "Current plan"
                : "Included in Professional"}
            </div>
          </article>
          <article className="membership-card membership-card--professional">
            <span className="membership-popular">Team plan</span>
            <span className="membership-plan-label">
              <TeamOutlined /> Professional
            </span>
            <div className="membership-price">
              <strong>${membershipPrice.toFixed(0)}</strong>
              <span>/ month</span>
            </div>
            <p>
              Build your own group and manage collaboration from one workspace.
            </p>
            <ul>
              {[
                "Everything in Standard",
                "Create and own a private group",
                "Invite users to your team",
                "Team interviews, calendar and statistics",
                "Analyze page access",
              ].map((feature) => (
                <li key={feature}>
                  <CheckCircleFilled /> {feature}
                </li>
              ))}
            </ul>
            {membershipStatus?.active ? (
              <div className="membership-active-label">
                Active until{" "}
                {new Date(
                  membershipStatus.membership.endsAt,
                ).toLocaleDateString()}
              </div>
            ) : isAuthenticated ? (
              Number(authUser?.role) !== 1 && (
                <Button
                  type="primary"
                  loading={purchasingMembership}
                  onClick={purchaseProfessional}
                >
                  Upgrade to Professional
                </Button>
              )
            ) : (
              <Link className="membership-signin-button" to="/login">
                Sign in to upgrade <ArrowRightOutlined />
              </Link>
            )}
          </article>
        </div>
      </section>
      <header className="pricing-hero">
        <div className="pricing-eyebrow">
          <ThunderboltFilled /> Simple, usage-based pricing
        </div>
        <h1>Only pay for what helps you apply.</h1>
        <p>
          Generation remains usage-based with no hidden fees. Create exactly
          what you need, whether you use Standard or the optional team plan.
        </p>
        <div className="pricing-trust-row" aria-label="Pricing benefits">
          <span>
            <CheckCircleFilled /> Free Standard plan
          </span>
          <span>
            <CheckCircleFilled /> Clear per-use rates
          </span>
          <span>
            <CheckCircleFilled /> Export files included
          </span>
        </div>
      </header>

      <section
        className="pricing-card-grid"
        aria-label="Resume Builder pricing"
      >
        {services.map((service) => (
          <article
            className={`pricing-service-card pricing-service-card--${service.key}`}
            key={service.key}
          >
            {Number(prices[service.priceKey]) < service.originalPrice && (
              <span className="pricing-card-badge">
                {Math.round(
                  (1 -
                    Number(prices[service.priceKey]) / service.originalPrice) *
                    100,
                )}
                % OFF
              </span>
            )}
            {service.badge && (
              <span className="pricing-card-badge">{service.badge}</span>
            )}
            <div className="pricing-card-icon">{service.icon}</div>
            <span className="pricing-card-eyebrow">{service.eyebrow}</span>
            <h2>{service.title}</h2>
            <p className="pricing-card-description">{service.description}</p>
            <div className="pricing-card-price">
              <span className="pricing-original-price">
                {formatPrice(service.originalPrice)}
              </span>
              <strong>{formatPrice(prices[service.priceKey])}</strong>
              <span className="pricing-price-unit">{service.unit}</span>
            </div>
            <ul>
              {service.features.map((feature) => (
                <li key={feature}>
                  <CheckCircleFilled /> <span>{feature}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </section>

      <section className="pricing-example">
        <div className="pricing-example-copy">
          <span className="pricing-card-eyebrow">A real-world example</span>
          <h2>
            A complete tailored application for just {formatPrice(exampleTotal)}
          </h2>
          <p>
            That includes five AI-assisted answers, one tailored resume in DOCX
            and PDF, and one tailored cover letter in PDF.
          </p>
        </div>
        <div
          className="pricing-example-math"
          aria-label="Example price calculation"
        >
          <div>
            <span>5 application answers</span>
            <b>{formatPrice(prices.answer * 5)}</b>
          </div>
          <div>
            <span>1 tailored resume</span>
            <b>{formatPrice(prices.resume)}</b>
          </div>
          <div>
            <span>1 tailored cover letter</span>
            <b>{formatPrice(prices.coverLetter)}</b>
          </div>
          <div className="pricing-example-total">
            <span>Total</span>
            <b>{formatPrice(exampleTotal)}</b>
          </div>
        </div>
      </section>

      <section className="pricing-cta">
        <div className="pricing-cta-icon">
          <SafetyCertificateOutlined />
        </div>
        <div>
          <h2>Ready to build your next application?</h2>
          <p>Start free and use AI only when you need it.</p>
        </div>
        <Link
          className="pricing-cta-button"
          to={isAuthenticated ? "/bid" : "/register"}
        >
          Get started <ArrowRightOutlined />
        </Link>
      </section>

      <section className="pricing-faq">
        <div className="pricing-faq-heading">
          <div className="pricing-faq-icon">
            <QuestionCircleOutlined />
          </div>
          <span className="pricing-card-eyebrow">Good to know</span>
          <h2>Questions about pricing?</h2>
          <p>
            Everything you need to know about charges, generated files, and
            pay-as-you-go usage.
          </p>
          <div className="pricing-faq-note">
            <CheckCircleFilled />
            <span>
              <strong>Standard stays free</strong> — usage charges apply only
              when new content is generated.
            </span>
          </div>
        </div>
        <Collapse
          accordion
          bordered={false}
          defaultActiveKey={["1"]}
          expandIconPosition="end"
          items={faqItems}
        />
      </section>
    </div>
  );
};

export default Pricing;
